import React, { useState } from 'react';
import { ArrowRight, Shield, Cpu, FlaskConical, ChevronRight, Upload, X, GraduationCap, Activity, Lock, Phone, Mail, MapPin } from 'lucide-react';
import axios from 'axios';
import '../styles/rivera.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = '/rivera-ri-logo.png';

const Home = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organization: '',
    message: '',
    inquiryType: 'capabilities'
  });
  const [uploadedFile, setUploadedFile] = useState(null);
  const [showVaultModal, setShowVaultModal] = useState(false);
  const [vaultRequest, setVaultRequest] = useState({ email: '', reason: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
      if (!allowedTypes.includes(file.type)) {
        alert('Invalid file type. Please upload PDF, DOC, or DOCX files only.');
        return;
      }
      if (file.size > 10 * 1024 * 1024) {
        alert('File size exceeds 10MB limit.');
        return;
      }
      setUploadedFile(file);
    }
  };

  const removeFile = () => setUploadedFile(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('name', formData.name);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('organization', formData.organization);
      formDataToSend.append('inquiry_type', formData.inquiryType);
      formDataToSend.append('message', formData.message);
      if (uploadedFile) {
        formDataToSend.append('file', uploadedFile);
      }
      const response = await axios.post(`${API}/contact`, formDataToSend, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.success) {
        setSubmitMessage(response.data.message);
        setFormData({ name: '', email: '', organization: '', message: '', inquiryType: 'capabilities' });
        setUploadedFile(null);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitMessage(error.response?.data?.detail || 'An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVaultRequest = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const formDataToSend = new FormData();
      formDataToSend.append('email', vaultRequest.email);
      formDataToSend.append('reason', vaultRequest.reason);
      const response = await axios.post(`${API}/vault-request`, formDataToSend);
      if (response.data.success) {
        alert(response.data.message);
        setShowVaultModal(false);
        setVaultRequest({ email: '', reason: '' });
      }
    } catch (error) {
      console.error('Error submitting portal request:', error);
      alert(error.response?.data?.detail || 'An error occurred. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="rivera-container">
      {/* Header */}
      <header className="rivera-header" data-testid="site-header">
        <a href="#home" className="rivera-logo-container" data-testid="logo-link">
          <img src={LOGO_URL} alt="Rivera Industries RI monogram" className="rivera-logo-mark" />
          <span className="rivera-logo-text">RIVERA INDUSTRIES</span>
        </a>
        <nav className="rivera-nav">
          <a href="#home" className="rivera-nav-link" data-testid="nav-home">Home</a>
          <a href="#series-s" className="rivera-nav-link" data-testid="nav-series-s">Applied Sciences</a>
          <a href="#series-t" className="rivera-nav-link" data-testid="nav-series-t">Frontier Tech</a>
          <a href="#academic" className="rivera-nav-link" data-testid="nav-academic">Academic Infrastructure</a>
          <a href="#corporate" className="rivera-nav-link" data-testid="nav-corporate">Corporate Profile</a>
          <button className="rivera-vault-btn" onClick={() => setShowVaultModal(true)} data-testid="portal-login-btn">
            <Lock size={16} />
            Secure Portal Login
          </button>
        </nav>
      </header>

      {/* Hero Section */}
      <section id="home" className="rivera-hero" data-testid="hero-section">
        <div className="rivera-hero-content">
          <div className="rivera-hero-text">
            <div className="rivera-eyebrow" data-testid="hero-eyebrow">
              <span className="rivera-eyebrow-dot" />
              DUAL-USE DEEP-TECH R&amp;D · SERIES LLC
            </div>
            <h1 className="rivera-hero-title" data-testid="hero-title">
              Engineering Next-Generation Dual-Use Materials and Computational Architecture
            </h1>
            <p className="rivera-hero-subtitle" data-testid="hero-subtitle">
              Rivera Industries accelerates advanced research, validation testing, and secure hardware
              deployment to fortify industrial manufacturing and defense infrastructure.
            </p>
            <div className="rivera-hero-buttons">
              <a href="#corporate" className="rivera-btn-primary" data-testid="cta-capabilities-brief">
                Review Capabilities Brief
                <ArrowRight size={20} />
              </a>
              <button className="rivera-btn-secondary" onClick={() => setShowVaultModal(true)} data-testid="cta-secure-portal">
                Secure Portal Login
                <ChevronRight size={20} />
              </button>
            </div>
            <div className="rivera-hero-stats" data-testid="hero-stats">
              <div className="rivera-stat">
                <span className="rivera-stat-value">26,892</span>
                <span className="rivera-stat-label">Sq. Ft. Laboratory Compound</span>
              </div>
              <div className="rivera-stat">
                <span className="rivera-stat-value">02</span>
                <span className="rivera-stat-label">Segregated Statutory Series</span>
              </div>
              <div className="rivera-stat">
                <span className="rivera-stat-value">NE OHIO</span>
                <span className="rivera-stat-label">Greater Akron Area</span>
              </div>
            </div>
          </div>
          <div className="rivera-hero-image">
            <img
              src="https://images.unsplash.com/photo-1656331797721-b593b8f00297?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwxfHxtb2xlY3VsYXIlMjBzdHJ1Y3R1cmV8ZW58MHx8fHwxNzY3NjI5ODY1fDA&ixlib=rb-4.1.0&q=85"
              alt="Advanced materials lattice structure"
            />
            <div className="rivera-hero-image-tag">CLASSIFIED-READY VALIDATION ENVIRONMENT</div>
          </div>
        </div>
      </section>

      {/* Series LLC Architecture */}
      <section id="architecture" className="rivera-section rivera-hubs" data-testid="architecture-section">
        <div className="rivera-section-content">
          <h2 className="rivera-section-title">The Series LLC Architecture</h2>
          <p className="rivera-section-intro">
            Rivera Industries operates two primary corporate tracks under segregated statutory series
            frameworks. Each track maintains dedicated laboratory infrastructure, validation protocols,
            and asset insulation for dual-use technology development.
          </p>

          <div className="rivera-hubs-grid">
            {/* Track Alpha — Series S */}
            <div id="series-s" className="rivera-hub-card" data-testid="series-s-card">
              <div className="rivera-hub-header">
                <div className="rivera-hub-icon"><FlaskConical size={32} /></div>
                <div className="rivera-hub-label">TRACK ALPHA · SERIES S</div>
              </div>
              <h3 className="rivera-hub-title">Advanced Materials &amp; Polymeric Validation</h3>
              <p className="rivera-hub-location">Applied Sciences · Solid-State Chemistry</p>
              <div className="rivera-hub-image">
                <img
                  src="https://images.unsplash.com/photo-1655993810480-c15dccf9b3a0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHxtb2xlY3VsYXIlMjBzdHJ1Y3R1cmV8ZW58MHx8fHwxNzY3NjI5ODY1fDA&ixlib=rb-4.1.0&q=85"
                  alt="Molecular durability structure"
                />
              </div>
              <p className="rivera-hub-description">
                Specialized commercialization and stress-testing of advanced <strong>self-healing
                vitrimers</strong>, high-durability polymers, and bio-synthetic materials for aerospace
                and military applications. Capabilities span destructive and non-destructive testing
                with full validation telemetry.
              </p>
              <div className="rivera-hub-capabilities">
                <h4>Validation Scope:</h4>
                <ul>
                  <li>Solid-state chemistry &amp; advanced material synthesis</li>
                  <li>Self-healing vitrimer commercialization</li>
                  <li>Stress-strain &amp; molecular durability metrics</li>
                  <li>Destructive / non-destructive testing protocols</li>
                </ul>
              </div>
            </div>

            {/* Track Beta — Series T */}
            <div id="series-t" className="rivera-hub-card" data-testid="series-t-card">
              <div className="rivera-hub-header">
                <div className="rivera-hub-icon rivera-hub-icon-cyber"><Cpu size={32} /></div>
                <div className="rivera-hub-label">TRACK BETA · SERIES T</div>
              </div>
              <h3 className="rivera-hub-title">Computational Architecture &amp; Neuromorphic AI</h3>
              <p className="rivera-hub-location rivera-cyber">Frontier Tech · Non-von Neumann Compute</p>
              <div className="rivera-hub-image">
                <img
                  src="https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxuZXVyYWwlMjBuZXR3b3JrfGVufDB8fHx8MTc2NzYyOTg3MXww&ixlib=rb-4.1.0&q=85"
                  alt="Neuromorphic circuit matrix"
                />
              </div>
              <p className="rivera-hub-description">
                Development and deployment of hardware-native <strong>Spiking Neural Networks (SNN)</strong>,
                bio-inspired cognitive computing nodes, and secure localized data processing for critical
                defense assets. Hardware-localized edge arrays operating on non-von Neumann architecture.
              </p>
              <div className="rivera-hub-capabilities">
                <h4>Compute Scope:</h4>
                <ul>
                  <li>Hardware-localized edge computing arrays</li>
                  <li>Hardware-native Spiking Neural Networks (SNN)</li>
                  <li>Bio-inspired cognitive computing nodes</li>
                  <li>Secure localized data processing for defense assets</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Academic Infrastructure — Rivera Fellows */}
      <section id="academic" className="rivera-section rivera-vision" data-testid="academic-section">
        <div className="rivera-section-content">
          <h2 className="rivera-section-title">Institutional Integration: The Rivera Fellows Program</h2>
          <div className="rivera-vision-grid">
            <div className="rivera-vision-text">
              <p className="rivera-body-large">
                Through formal institutional research partnerships, Rivera Industries bridges
                academic breakthrough with commercial prototyping.
              </p>
              <p className="rivera-body-medium">
                The Rivera Fellows program establishes a continuous, elite pipeline of PhD candidates and
                graduate research assistants operating inside our advanced laboratories, accelerating
                innovations in polymer physics and computational data models.
              </p>
              <div className="rivera-fellows-points">
                <div className="rivera-fellows-point">
                  <GraduationCap size={20} />
                  <span>Elite PhD &amp; graduate research assistant pipeline</span>
                </div>
                <div className="rivera-fellows-point">
                  <Activity size={20} />
                  <span>Polymer physics &amp; computational data model research</span>
                </div>
                <div className="rivera-fellows-point">
                  <Shield size={20} />
                  <span>Institutional research partnerships &amp; collaboration</span>
                </div>
              </div>
            </div>
            <div className="rivera-vision-image">
              <img
                src="https://images.unsplash.com/photo-1504625709867-b4e45e3bb9dd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxicnV0YWxpc3QlMjBhcmNoaXRlY3R1cmV8ZW58MHx8fHwxNzY3NjI5ODc4fDA&ixlib=rb-4.1.0&q=85"
                alt="Institutional research infrastructure"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Corporate Profile / Capabilities Brief Request */}
      <section id="corporate" className="rivera-section rivera-contact" data-testid="corporate-section">
        <div className="rivera-section-content">
          <h2 className="rivera-section-title">Corporate Profile &amp; Inquiries</h2>
          <p className="rivera-section-intro">
            Request our Capabilities Brief, submit RFPs, or apply to the Rivera Fellows program. Procurement
            officials, program managers, and PhD candidates are routed to the appropriate division.
          </p>

          <form onSubmit={handleSubmit} className="rivera-contact-form" data-testid="contact-form">
            <div className="rivera-form-row">
              <div className="rivera-form-group">
                <label>Full Name *</label>
                <input type="text" name="name" value={formData.name} onChange={handleInputChange} required placeholder="Dr. Jane Smith" data-testid="contact-name" />
              </div>
              <div className="rivera-form-group">
                <label>Email Address *</label>
                <input type="email" name="email" value={formData.email} onChange={handleInputChange} required placeholder="j.smith@institution.edu" data-testid="contact-email" />
              </div>
            </div>

            <div className="rivera-form-group">
              <label>Organization / Institution</label>
              <input type="text" name="organization" value={formData.organization} onChange={handleInputChange} placeholder="University / Agency / Corporation" data-testid="contact-organization" />
            </div>

            <div className="rivera-form-group">
              <label>Inquiry Type *</label>
              <select name="inquiryType" value={formData.inquiryType} onChange={handleInputChange} required data-testid="contact-inquiry-type">
                <option value="capabilities">Capabilities Brief Request</option>
                <option value="materials">Series S — Advanced Materials &amp; Validation</option>
                <option value="compute">Series T — Computational Architecture &amp; AI</option>
                <option value="fellows">Rivera Fellows Program (PhD / Graduate)</option>
                <option value="program">Program Management</option>
                <option value="rfp">RFP / Procurement</option>
              </select>
            </div>

            <div className="rivera-form-group">
              <label>Message *</label>
              <textarea name="message" value={formData.message} onChange={handleInputChange} required rows="5" placeholder="Describe your requirement, research background, or procurement scope..." data-testid="contact-message" />
            </div>

            <div className="rivera-form-group">
              <label>Attach CV / RFP Document (Optional)</label>
              <div className="rivera-file-upload">
                <input type="file" id="fileUpload" onChange={handleFileUpload} accept=".pdf,.doc,.docx" style={{ display: 'none' }} data-testid="contact-file-input" />
                <label htmlFor="fileUpload" className="rivera-file-label">
                  <Upload size={20} />
                  {uploadedFile ? 'Change File' : 'Upload Document'}
                </label>
                {uploadedFile && (
                  <div className="rivera-file-info">
                    <span data-testid="contact-file-name">{uploadedFile.name}</span>
                    <button type="button" onClick={removeFile} className="rivera-file-remove" data-testid="contact-file-remove">
                      <X size={16} />
                    </button>
                  </div>
                )}
              </div>
              <p className="rivera-file-hint">PDF, DOC, or DOCX • Max 10MB</p>
            </div>

            <button type="submit" className="rivera-btn-submit" disabled={isSubmitting} data-testid="contact-submit">
              {isSubmitting ? 'Submitting...' : 'Submit Inquiry'}
              <ArrowRight size={20} />
            </button>

            {submitMessage && (
              <div className={`rivera-submit-message ${submitMessage.includes('error') || submitMessage.includes('occurred') ? 'error' : 'success'}`} data-testid="contact-submit-message">
                {submitMessage}
              </div>
            )}
          </form>
        </div>
      </section>

      {/* Footer */}
      <footer className="rivera-footer" data-testid="site-footer">
        <div className="rivera-footer-content">
          <div className="rivera-footer-brand">
            <img src={LOGO_URL} alt="Rivera Industries RI monogram" className="rivera-logo-mark rivera-logo-mark-footer" />
            <div className="rivera-footer-text">
              <h3>RIVERA INDUSTRIES LLC</h3>
              <p>Dual-use technology validation across segregated statutory series frameworks.</p>
            </div>
          </div>
          <div className="rivera-footer-links">
            <div className="rivera-footer-column">
              <h4>Divisions</h4>
              <ul>
                <li><a href="#series-s">Applied Sciences (Series S)</a></li>
                <li><a href="#series-t">Frontier Tech (Series T)</a></li>
                <li><a href="#academic">Academic Infrastructure</a></li>
              </ul>
            </div>
            <div className="rivera-footer-column">
              <h4>Corporate</h4>
              <ul>
                <li><a href="#corporate">Corporate Profile</a></li>
                <li><a href="#corporate">Capabilities Brief</a></li>
                <li><button className="rivera-footer-linkbtn" onClick={() => setShowVaultModal(true)}>Secure Portal Login</button></li>
              </ul>
            </div>
            <div className="rivera-footer-column">
              <h4>Contact</h4>
              <ul className="rivera-footer-contact">
                <li><Phone size={14} /> (330) 880-8524</li>
                <li><Mail size={14} /> Inquiries@RiveraInd.com</li>
                <li><MapPin size={14} /> Serving the Greater Akron Area, Northeast Ohio</li>
              </ul>
            </div>
          </div>
        </div>
        <div className="rivera-footer-disclaimer" data-testid="footer-disclaimer">
          Operational divisions are partitioned under segregated statutory series frameworks to optimize
          asset security and liability insulation.
        </div>
        <div className="rivera-footer-bottom">
          <p>© 2026 Rivera Industries LLC. All Rights Reserved. Operations are secure, compliant, and optimized for dual-use technology validation.</p>
        </div>
      </footer>

      {/* Secure Portal Modal */}
      {showVaultModal && (
        <div className="rivera-modal-overlay" onClick={() => setShowVaultModal(false)} data-testid="portal-modal">
          <div className="rivera-modal" onClick={(e) => e.stopPropagation()}>
            <div className="rivera-modal-header">
              <h3>Secure Portal Access</h3>
              <button onClick={() => setShowVaultModal(false)} className="rivera-modal-close" data-testid="portal-modal-close">
                <X size={24} />
              </button>
            </div>
            <form onSubmit={handleVaultRequest} className="rivera-modal-form">
              <p className="rivera-modal-description">
                The Secure Portal provides credentialed partners and procurement officials access to
                classified-ready technical abstracts, validation data, and collaboration frameworks.
                Access is provisioned on a verified, case-by-case basis.
              </p>
              <div className="rivera-form-group">
                <label>Email Address *</label>
                <input type="email" value={vaultRequest.email} onChange={(e) => setVaultRequest({ ...vaultRequest, email: e.target.value })} required placeholder="your.email@organization.com" data-testid="portal-email" />
              </div>
              <div className="rivera-form-group">
                <label>Reason for Access *</label>
                <textarea value={vaultRequest.reason} onChange={(e) => setVaultRequest({ ...vaultRequest, reason: e.target.value })} required rows="4" placeholder="Identify your organization and intended use of secured materials..." data-testid="portal-reason" />
              </div>
              <button type="submit" className="rivera-btn-submit" disabled={isSubmitting} data-testid="portal-submit">
                {isSubmitting ? 'Submitting...' : 'Request Access'}
                <Lock size={20} />
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Home;
